import React from 'react';
import {
  Chip,
  Grid,
  styled,
  Table,
  SxProps,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { Star } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { AccountData, EventPassHolderOut, EventPassHoldersOut } from '../../../../api';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../common/GeneralUtils';
import { getDateFormat } from '../../../../common/getDateAndTimeFormat';
import { getPassOrderPage } from '../../../../utils/AccountUtils';
import RotatingLoader from '../../../../common/ui/RotatingLoader';

type EventPassHoldsTableProps = {
  holders: EventPassHolderOut[];
  weeklyHoldersCount: EventPassHoldersOut['weekly_holders_count'];
  slotCapacity: EventPassHoldersOut['slot_capacity'];
  isLoading: boolean;
};

const StyledHoldsTable = styled(Table)(() => ({
  whiteSpace: 'pre',
  '& th, & td': { paddingInlineStart: 16 },
}));

const holdRowSx = (holder: EventPassHolderOut): SxProps<Theme> | undefined =>
  holder.permanent_schedule_hold
    ? {
        backgroundColor: (theme: Theme) => theme.palette.mb.claySoft,
        '& td:first-of-type': {
          boxShadow: (theme: Theme) =>
            `inset ${theme.direction === 'rtl' ? '-4px' : '4px'} 0 0 ${theme.palette.warning.main}`,
          paddingInlineStart: '16px',
        },
      }
    : undefined;

const EventPassHoldsTable = ({ holders, weeklyHoldersCount, slotCapacity, isLoading }: EventPassHoldsTableProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const dateFormat = getDateFormat(account).longDateFormat;

  const formatDate = (date: string) => moment(date).format(dateFormat);

  const passEndCell = (holder: EventPassHolderOut) => {
    if (holder.permanent_schedule_hold) {
      return (
        <Typography variant="body2" color="warning.main">
          {t('calendar.event_popper.pass_holds.until_permanent')}
        </Typography>
      );
    }
    // a scheduled change or an earlier release frees the seat before the pass itself ends
    const freesEarly = !!holder.holds_until && holder.holds_until < holder.pass_end_date;
    return (
      <Grid container flexDirection="column" alignItems="center">
        <Typography variant="body2">{formatDate(holder.pass_end_date)}</Typography>
        {freesEarly && (
          <Typography variant="caption" color="text.secondary">
            {t('calendar.event_popper.pass_holds.frees_early', { date: formatDate(holder.holds_until!) })}
          </Typography>
        )}
      </Grid>
    );
  };

  const statusChip = (holder: EventPassHolderOut) => {
    if (holder.canceled_booking) {
      return <Chip size="small" color="warning" label={t('calendar.event_popper.pass_holds.signed_off')} />;
    }
    if (holder.has_booking) {
      return <Chip size="small" variant="outlined" label={t('calendar.event_popper.pass_holds.booked')} />;
    }
    return <Chip size="small" variant="outlined" label={t('calendar.event_popper.pass_holds.not_booked')} />;
  };

  if (isLoading) {
    return (
      <Grid container justifyContent="center" p={3}>
        <RotatingLoader />
      </Grid>
    );
  }

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" gap={1} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {t('calendar.event_popper.pass_holds.weekly_slot_summary', {
          holders: weeklyHoldersCount,
          capacity: slotCapacity,
        })}
      </Typography>
      <Grid
        container
        flexDirection="column"
        flexWrap="nowrap"
        border="1px solid"
        borderColor="divider"
        borderRadius="8px"
        sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <StyledHoldsTable size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="inherit">{t('orders.name')}</TableCell>
              <TableCell align="center">{t('calendar.event_popper.pass_holds.pass_product')}</TableCell>
              <TableCell align="center">{t('calendar.event_popper.pass_holds.pass_end_date')}</TableCell>
              <TableCell align="center">{t('orders.status')}</TableCell>
              <TableCell align="center">{t('orders.order_number')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {holders.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t('calendar.event_popper.pass_holds.none_on_this_date')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {holders.map((holder) => {
              const passOrderPage = getPassOrderPage(account, holder.pass_order_page_id);
              const name = `${holder.firstname} ${holder.lastname}`;
              return (
                <TableRow key={holder.pass_id} sx={holdRowSx(holder)}>
                  <TableCell align="inherit">
                    <Grid container alignItems="center" gap={0.5} flexWrap="nowrap">
                      {holder.permanent_schedule_hold && (
                        <Tooltip title={t('passes.permanent_schedule_hold_toggle')} arrow>
                          <Star fontSize="small" sx={{ color: 'warning.main' }} />
                        </Tooltip>
                      )}
                      <Link
                        to={`/dashboard/passes?search=${encodeURIComponent(name)}`}
                        style={{ color: 'inherit', textDecoration: 'underline' }}>
                        {name}
                      </Link>
                    </Grid>
                  </TableCell>
                  <TableCell align="center">
                    {passOrderPage?.label?.[language] || passOrderPage?.label?.en || ''}
                  </TableCell>
                  <TableCell align="center">{passEndCell(holder)}</TableCell>
                  <TableCell align="center">{statusChip(holder)}</TableCell>
                  <TableCell align="center">{holder.order_number}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </StyledHoldsTable>
      </Grid>
    </Grid>
  );
};

export default EventPassHoldsTable;
